import { isNullOrEmpty, paymentTypes } from './constants';

export class Validate {


  // ==============================================
  // BANK DETAILS VALIDATION
  // ==============================================

   /**
   * Validates that at least one bank detail has been added
   */
  static anyBankDetails(bankDataList) {
    if (!bankDataList || bankDataList.length === 0) {
      return {
        isValid: false,
        message: "Please add at least one bank detail."
      };
    }
    return { isValid: true };
  }

  /**
   * Validates if a field is required and not empty
   */
  static required(value, fieldName) {
    if (isNullOrEmpty(value)) {
      return {
        isValid: false,
        message: `${fieldName} is required`
      };
    }
    return { isValid: true };
  }


  /**
   * Validates that a string doesn't contain hyphens
   */
  static noHyphens(value, fieldName = "Field") {
    if (value && value.indexOf("-") !== -1) {
      return {
        isValid: false,
        message: `Hyphens (-) are not allowed in ${fieldName}. Please remove the hyphen and try again.`
      };
    }
    return { isValid: true };
  }


  /**
   * Validates that a string doesn't contain ampersands (&)
   */
  static noAmpersands(value, fieldName = "Field") {
    if (value && value.indexOf("&") !== -1) {
      return {
        isValid: false,
        message: `Ampersand (&) is not allowed in ${fieldName}`
      };
    }
    return { isValid: true };
  }


  /**
   * Validates field length
   */
  static length(value, requiredLength, fieldName) {
    if (value && value.length < requiredLength || value.length > requiredLength) {
      return {
        isValid: false,
        message: `${fieldName} must have exactly ${requiredLength} numeric characters`
      };
    }
    return { isValid: true };
  }


  /**
   * Validates exact length
   */
  static exactLength(value, exactLength, fieldName) {
    if (value && value.length !== exactLength) {
      return {
        isValid: false,
        message: `${fieldName} requires ${exactLength} characters`
      };
    }
    return { isValid: true };
  }


  /**
   * Validates text against a regex pattern
   */
  static pattern(value, pattern, errorMessage) {
    if (value && pattern.test(value)) {
      return {
        isValid: false,
        message: errorMessage
      };
    }
    return { isValid: true };
  }


  /**
   * Validates that values are different from each other
   */
  static uniqueValues(values, valueNames) {
    const fieldValues = values.filter(v => v && v.trim() !== "");
    const fieldNames = valueNames.filter((name, index) => values[index] && values[index].trim() !== "");

    if (fieldValues.length <= 1) {
      return { isValid: true };
    }

    // Check if all non-empty values are the same
    if (fieldValues.length === 3 && fieldValues.every(v => v === fieldValues[0])) {
      return {
        isValid: false,
        message: `${fieldNames.join(", ")} cannot all have the same value.`
      };
    }

    // Check pairs
    for (let i = 0; i < fieldValues.length; i++) {
      for (let j = i + 1; j < fieldValues.length; j++) {
        if (fieldValues[i] === fieldValues[j]) {
          return {
            isValid: false,
            message: `${fieldNames[i]} and ${fieldNames[j]} cannot have the same value.`
          };
        }
      }
    }

    return { isValid: true };
  }


  /**
   * Validates IBAN requirements
   */
  static iban(paymentType, iban) {
    if (paymentType === paymentTypes.IBAN) {
      return this.required(iban, "IBAN");
    }
    return { isValid: true };
  }


  /**
   * Validates SWIFT account number requirements
   */
  static swiftAccountNumber(paymentType, accountNumber) {
    if (paymentType === paymentTypes.SWIFT) {
      return this.required(accountNumber, "Account Number for SWIFT payment type");
    }
    return { isValid: true };
  }


  /**
   * Validates required fields for IBAN form
   */
  static requiredFieldsForIban(iban, beneficiaryName) {
    const missingFields = [];

    if (!iban || iban.trim() === "") {
      missingFields.push("IBAN Account Number");
    }

    if (!beneficiaryName || beneficiaryName.trim() === "") {
      missingFields.push("Beneficiary Name");
    }

    return missingFields;
  }


  /**
   * Validates required fields for SWIFT form
   */
  static requiredFieldsForSwift(currency, accountNumber, bic, beneficiaryName) {
    const missingFields = [];

    // Check Currency
    if (!currency || currency.trim() === "") {
      missingFields.push("Currency");
    }

    // Check Account Number
    if (!accountNumber || accountNumber.trim() === "") {
      missingFields.push("Account Number");
    }

    // Check Beneficiary Bank SWIFT BIC
    if (!bic || bic.trim() === "") {
      missingFields.push("Beneficiary Bank SWIFT BIC");
    }

    // Check Beneficiary Name
    if (!beneficiaryName || beneficiaryName.trim() === "") {
      missingFields.push("Beneficiary Name");
    }

    return missingFields;
  }


  /**
   * Validates required fields for REGNO/ACCOUNT form
   */
  static requiredFieldsForRegnoAccount(regNo, accountNumber) {
    const missingFields = [];

    if (!regNo || regNo.trim() === "") {
      missingFields.push("Registration Number");
    }

    if (!accountNumber || accountNumber.trim() === "") {
      missingFields.push("Account Number");
    }

    return missingFields;
  }


  /**
   * Validates FIK number length (must be 7 or 8 digits)
   */
  static validateFikLength(fikNumber) {
    const trimmed = fikNumber ? fikNumber.trim() : "";
    if (!/^\d{7,8}$/.test(trimmed)) {
      return {
        isValid: false,
        message: "Kreditor/GIRO must have exactly 7 or 8 numeric characters"
      };
    }
    return { isValid: true };
  }


  /**
   * Validates required fields for FIK form
   */
  static requiredFieldsForFik(fikNumber) {
    const missingFields = [];

    if (!fikNumber || fikNumber.trim() === "") {
      missingFields.push("Kreditor/GIRO");
    }

    return missingFields;
  }


  /**
   * Sanitizes beneficiary name by removing invalid characters
   */
  static sanitizeBeneficiaryName(value) {
    const beneficiaryPattern = /[^a-zA-Z0-9 (),.&]/g;
    return (value || "").replace(beneficiaryPattern, "");
  }


  /**
   * Sanitizes account number by removing invalid characters  
   */
  static sanitizeAccountNumber(value) {
    const accountNumberPattern = /[^a-zA-Z0-9]/g;
    return (value || "").replace(accountNumberPattern, "");
  }


  /**
   * Formats FIK number with proper prefix
   */
  static formatFikNumber(fikNumber) {
    if (!fikNumber) return fikNumber;

    const firstNumber = fikNumber.substring(0, 1);
    if (fikNumber.length === 7 && firstNumber !== "0") {
      return `+0${fikNumber}`;
    } else {
      return `+${fikNumber}`;
    }
  }


  /**
   * Validates beneficiary name for IBAN and SWIFT payment types
   */
  static validateBeneficiaryName(beneficiaryName, paymentType) {
    const beneficiaryPattern = /[^a-zA-Z0-9 (),.&]/g;

    // Required validation
    const required = this.required(beneficiaryName?.trim(), "Beneficiary Name");
    if (!required.isValid) return required;

    // Ampersand validation
    const noAmpersands = this.noAmpersands(beneficiaryName, "Beneficiary Name");
    if (!noAmpersands.isValid) return noAmpersands;

    // Pattern validation
    const pattern = this.pattern(
      beneficiaryName || "",
      beneficiaryPattern,
      "Invalid characters in beneficiary name"
    );
    if (!pattern.isValid) return pattern;

    return { isValid: true };
  }


  /**
   * Validates account number pattern for special characters
   */
  static validateAccountNumberPattern(accountNumber) {
    const accountNumberPattern = /[^a-zA-Z0-9 ]/g;
    return accountNumberPattern.test(accountNumber);
  }


  /**
   * Strips the "XXX" primary office branch code so it compares equal to its 8-char equivalent
   */
  static normalizeBic(value) {
    if (!value) return value;
    const trimmed = value.trim().toUpperCase();
    return trimmed.length === 11 && trimmed.endsWith("XXX") ? trimmed.slice(0, 8) : trimmed;
  }


  /**
   * True when two SWIFT/BIC values only match once the "XXX" branch code is stripped
   */
  static bicsMatchOnlyAfterNormalizing(a, b) {
    if (!a || !b) return false;
    return a.trim().toUpperCase() !== b.trim().toUpperCase() && this.normalizeBic(a) === this.normalizeBic(b);
  }


  /**
   * Validates that SWIFT values are unique and returns validation result
   */
  static swiftDifferentValues(accountNumber, swift, correspondentBicSwift) {
    const result = this.uniqueValues(
      [accountNumber, this.normalizeBic(swift), this.normalizeBic(correspondentBicSwift)],
      ["Account Number", "Beneficiary Bank SWIFT BIC", "Correspondent Bank SWIFT BIC"]
    );

    if (!result.isValid && this.bicsMatchOnlyAfterNormalizing(swift, correspondentBicSwift)) {
      return {
        ...result,
        message: `${result.message} "XXX" marks the primary office branch — these are effectively the same bank.`
      };
    }

    return result;
  }


  /**
   * Validates SWIFT BIC validation required fields and returns which fields are invalid
   */
  static swiftBicRequiredFields(bankAccountNumber, bic, regNo, showRegNo) {
    const invalidFields = [];

    if (!bankAccountNumber || bankAccountNumber.trim() === "") {
      invalidFields.push("bankAccountNumber");
    }

    if (showRegNo && (!regNo || regNo.trim() === "")) {
      invalidFields.push("regNo");
    }

    if (!bic || bic.trim() === "") {
      invalidFields.push("bic");
    }

    return {
      isValid: invalidFields.length === 0,
      invalidFields
    };
  }

  // ==============================================
  // ACCOUNT INFORMATION VALIDATION
  // ==============================================

  /**
   * Validates required fields for account information
   */
  static requiredFieldsForAccountInfo(name, address1, city, zip, countryId, phone, paymentTerm, isPaymentTermVisible = false) {
    const missingFields = [];

    if (!name || name.trim() === '') {
      missingFields.push('Account Name');
    }
    if (!address1 || address1.trim() === '') {
      missingFields.push('Address Line 1');
    }
    if (!city || city.trim() === '') {
      missingFields.push('City');
    }
    if (!zip || zip.trim() === '') {
      missingFields.push('Postal Code');
    }
    if (!countryId) {
      missingFields.push('Country');
    }
    if (!phone || phone.trim() === '') {
      missingFields.push('Phone');
    }
    if (isPaymentTermVisible && (paymentTerm === undefined || paymentTerm === null || String(paymentTerm).trim() === '')) {
      missingFields.push('Payment terms in days');
    }

    return missingFields;
  }

  /**
   * Removes all spaces from a string
   */
  static removeSpaces(value) {
    if (!value) return value;
    return value.replace(/\s+/g, "");
  }

  /**
   * Formats tax registration number by removing spaces and limiting length
   */
  static formatTaxRegistrationNumber(value, maxLength = 20) {
    if (!value) return value;
    const noSpaces = this.removeSpaces(value);
    return noSpaces.length > maxLength ? noSpaces.substring(0, maxLength) : noSpaces;
  }

  /**
   * Validates and formats a field with maximum length
   */
  static maxLength(value, maxLength, fieldName) {
    if (!value) return { isValid: true, formattedValue: value };

    if (value.length > maxLength) {
      return {
        isValid: false,
        message: `${fieldName} cannot exceed ${maxLength} characters`,
        formattedValue: value.substring(0, maxLength)
      };
    }

    return { isValid: true, formattedValue: value };
  }

  // ==============================================
  // FILES PAGE VALIDATION
  // ==============================================

  /**
   * Validates that at least one file has been uploaded
   */
  static anyFiles(fileList) {
    if (!fileList || fileList.length === 0) {
      return {
        isValid: false,
        message: "Please attach a supporting document."
      };
    }
    return { isValid: true };
  }

  /**
   * Validates if a file with the same name already exists for a specific bank data
   */
  static duplicateFileName(fileName, bankDataId, existingFiles, bankDataDescription) {
    const existingFile = existingFiles.find(
      (file) => file.name === fileName && file.bankDataId === bankDataId
    );

    if (existingFile) {
      return {
        isValid: false,
        message: `File with same name already exists for ${bankDataDescription}`
      };
    }
    return { isValid: true };
  }

  /**
   * Validates file upload for multiple bank accounts
   */
  static filesForAllBanks(files, fileList, bankDataList, showBankDataDetails) {
    for (const file of files) {
      for (const bankData of bankDataList) {
        const existingFile = fileList.find(
          (existing) => existing.name === file.name && existing.bankDataId === bankData.id
        );

        if (existingFile) {
          return {
            isValid: false,
            message: `File "${file.name}" already exists for ${showBankDataDetails(bankData.id)}`
          };
        }
      }
    }
    return { isValid: true };
  }

  /**
   * Generates success message for file upload
   */
  static fileUploadSuccess(bankDataId, bankDataDescription, isMultipleBanks, bankCount) {
    if (bankDataId === -1) {
      return `Files uploaded successfully to all ${bankCount} bank accounts`;
    }
    return `Files uploaded successfully to ${bankDataDescription}`;
  }

  /**
   * Generates error message for file upload failure
   */
  static fileUploadError(error, bankDataId, isMultipleBanks) {
    const serverMessage = error.response?.data;
    if (bankDataId === -1) {
      return serverMessage || error.message || `Uploading files to all bank accounts failed. Try again.`;
    }
    return serverMessage || `Uploading file failed. Try again.`;
  }

  // ==============================================
  // REVIEW PAGE VALIDATION
  // ==============================================

  /**
   * Validates that all required data for submission is present
   */
  static requiredData(bankDataList, fileList) {
    const missingRequirements = [];

    // Check bank details
    if (!bankDataList || bankDataList.length === 0) {
      missingRequirements.push("at least one bank detail");
    }

    // Check files
    if (!fileList || fileList.length === 0) {
      missingRequirements.push("at least one supporting document");
    }

    if (missingRequirements.length > 0) {
      return {
        isValid: false,
        message: `Please add ${missingRequirements.join(" and ")} before submitting.`
      };
    }

    return { isValid: true };
  }

  /**
   * Validates that all required confirmations are checked
   */
  static requiredConfirmations(formConfirmed, formSOCConfirmed, formTOCConfirmed) {
    const missingConfirmations = [];

    if (!formConfirmed) {
      missingConfirmations.push("Personal Data Policy");
    }
    if (!formSOCConfirmed) {
      missingConfirmations.push("Supplier Code of Conduct");
    }
    if (!formTOCConfirmed) {
      missingConfirmations.push("General Terms and Conditions for Purchase of Goods & Services");
    }

    if (missingConfirmations.length > 0) {
      return {
        isValid: false,
        message: `You must accept the following before submitting the form: ${missingConfirmations.join(", ")}`
      };
    }

    return { isValid: true };
  }


}